<script>
import APIMcp from '@app/services/API/Mcp';
import Logo from '@app/components/Logo.vue';
import { describeMcpConnectionBlock, isMcpPlanBlock } from '@app/config/mcpAccess';
import { describeMcpTool } from '@app/config/mcpTools';

export default {
  name: 'OAuthConsent',
  components: { Logo },
  data: () => ({
    loading: true,
    submitting: false,
    error: null,
    context: null,
    approvedScopes: [],
  }),
  head: {
    title: 'Autoriser un assistant IA',
  },
  computed: {
    scopeLabels() {
      const writeTools = new Set(this.allowedToolsByScope['mcp:write'] || []);
      const sensitiveActions = [];

      if (writeTools.has('update_training')) {
        sensitiveActions.push('La publication d’une formation nécessite votre confirmation.');
      }
      if (writeTools.has('update_bundle')) {
        sensitiveActions.push('La publication d’un pack nécessite votre confirmation.');
      }
      if (writeTools.has('create_discount_code')) {
        sensitiveActions.push('Un code promo est actif dès sa création.');
      }
      if (writeTools.has('create_affiliate')) {
        sensitiveActions.push('Ajouter un affilié active son accès et peut lui envoyer une notification.');
      }

      const read = this.isLimitedAccess ? {
        label: 'Lister vos formations et rechercher dans le catalogue',
        description: 'L’assistant pourra voir les informations de catalogue et localiser des formations '
          + 'ou leçons. Il ne pourra pas ouvrir ni résumer leur contenu complet.',
      } : {
        label: 'Lire les données autorisées de votre espace',
        description: 'Seuls les outils de lecture listés ci-dessous seront disponibles. Les résultats peuvent '
          + 'contenir les identités nécessaires à votre demande lorsque votre rôle l’autorise.',
      };

      return {
        'mcp:read': read,
        'mcp:stats': {
          label: 'Consulter les statistiques autorisées',
          description: 'Seuls les outils statistiques listés ci-dessous seront disponibles pour votre rôle.',
        },
        'mcp:write': {
          label: 'Créer, modifier et supprimer les éléments autorisés',
          description: `Seules les actions listées ci-dessous seront disponibles. Les suppressions et autres actions sensibles demandent une confirmation. ${sensitiveActions.join(' ')}`,
        },
      };
    },
    grantableScopes() {
      return this.context ? this.context.grantable_scopes : [];
    },
    isLimitedAccess() {
      return Boolean(this.context && this.context.limited_access);
    },
    allowedToolsByScope() {
      return (this.context && this.context.allowed_tools_by_scope) || {};
    },
    scopeToolDetails() {
      return this.grantableScopes.reduce((byScope, scope) => ({
        ...byScope,
        [scope]: (this.allowedToolsByScope[scope] || []).map(describeMcpTool),
      }), {});
    },
    canConnect() {
      if (!this.context) return false;
      if (typeof this.context.connection_allowed === 'boolean') {
        return this.context.connection_allowed;
      }
      return Boolean(this.context.plan_ok);
    },
    connectionBlockMessage() {
      const reason = this.context && this.context.connection_block_reason;
      return describeMcpConnectionBlock(reason);
    },
    isPlanBlocked() {
      return isMcpPlanBlock(this.context && this.context.connection_block_reason);
    },
    canManagePlan() {
      return this.$store.getters['auth/isRoleOwner'] || this.$store.getters['auth/isRoleAdmin'];
    },
  },
  async mounted() {
    try {
      this.context = await APIMcp.getConsentContext(this.$route.query);
      // Le consentement reste explicite : seuls les droits demandés et
      // accordables sont cochés, puis soumis par l'utilisateur.
      this.approvedScopes = [...this.grantableScopes];
    } catch (e) {
      this.error = 'Cette demande d\'autorisation est invalide ou a expiré. Relancez la connexion depuis votre assistant IA.';
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async switchAccount() {
      if (this.submitting) return;
      this.submitting = true;
      const redirect = this.$route.fullPath;
      try {
        await this.$store.dispatch('auth/logout');
        await this.$router.replace({ name: 'login', query: { redirect } });
      } catch (e) {
        this.$buefy.toast.open({ message: 'Impossible de changer de compte. Réessayez.', type: 'is-danger' });
        this.submitting = false;
      }
    },
    async approve() {
      if (this.submitting) return;
      this.submitting = true;
      try {
        const { redirect_url } = await APIMcp.approveConsent({
          ...this.$route.query,
          approved_scopes: this.approvedScopes,
        });
        window.location.assign(redirect_url);
      } catch (e) {
        const message = (e.response && e.response.data && e.response.data.message)
          || 'Une erreur est survenue. Réessayez.';
        this.$buefy.toast.open({ message, type: 'is-danger' });
        this.submitting = false;
      }
    },
    async deny() {
      if (this.submitting) return;
      this.submitting = true;
      try {
        const { redirect_url } = await APIMcp.denyConsent(this.$route.query);
        window.location.assign(redirect_url);
      } catch (e) {
        this.$buefy.toast.open({ message: 'Une erreur est survenue. Fermez simplement cet onglet.', type: 'is-danger' });
        this.submitting = false;
      }
    },
  },
};
</script>

<template>
  <section class="section has-background-light">
    <div class="container w-640">
      <div class="is-flex is-justify-content-center mb-5">
        <Logo />
      </div>
      <div v-if="loading" class="has-text-centered py-6">
        <b-loading :is-full-page="false" active />
      </div>

      <div v-else-if="error" class="box has-text-centered">
        <b-icon icon="exclamation-circle" size="is-large" type="is-danger" />
        <p class="mt-4">{{ error }}</p>
      </div>

      <div v-else class="box">
        <h1 class="title is-4 has-text-centered">
Autoriser l'accès à votre espace Teachizy
</h1>

        <div class="has-text-centered mb-5">
          <p>
            <strong>{{ context.client_name }}</strong> demande à se connecter à votre espace de formation.
          </p>
          <p class="help has-text-grey-dark">
            Après autorisation, vous serez renvoyé vers <strong>{{ context.redirect_host }}</strong>
          </p>
        </div>

        <b-message type="is-info">
          <p>Compte : <strong>{{ context.account.email }}</strong></p>
          <p v-if="context.school">
            Espace : <strong>{{ context.school.name }}</strong> ({{ context.school.domain }})
          </p>
          <p>Rôle : {{ $t(`roles.${context.account.role}`) }}</p>
          <b-button
            class="mt-3 has-text-black"
            size="is-small"
            :disabled="submitting"
            @click="switchAccount"
          >
            Utiliser un autre compte
          </b-button>
        </b-message>

        <b-message v-if="!canConnect" type="is-warning">
          <p class="has-text-weight-medium">{{ connectionBlockMessage }}</p>
          <p
            v-if="isPlanBlocked"
            class="mt-2"
          >
            <b-button
              v-if="canManagePlan"
              tag="router-link"
              :to="{ name: 'plan' }"
              type="is-primary"
              size="is-small"
              class="has-text-black"
            >
              Découvrir les forfaits compatibles
            </b-button>
            <span v-else>Contactez le propriétaire de votre espace pour changer de plan.</span>
          </p>
        </b-message>

        <template v-else>
          <b-message v-if="isLimitedAccess" type="is-warning">
            <p class="has-text-weight-bold mb-2">
              Accès limité — votre forfait autorise uniquement l’aperçu du catalogue
            </p>
            <p>
              Cet assistant pourra lister vos formations et effectuer une recherche, sans ouvrir les
              contenus complets, consulter les statistiques, créer ni modifier quoi que ce soit.
            </p>
            <b-button
              v-if="canManagePlan"
              class="mt-3"
              tag="router-link"
              :to="{ name: 'plan', query: { onglet: 'vous' } }"
              type="is-warning"
              size="is-small"
              icon-right="gem"
            >
              Débloquer l’accès complet
            </b-button>
          </b-message>

          <p class="has-text-weight-medium mb-3">Cet assistant pourra :</p>

          <div v-for="scope in grantableScopes" :key="scope" class="box mb-3">
            <b-checkbox v-model="approvedScopes" :native-value="scope" type="is-success">
              <span class="has-text-weight-medium">{{ scopeLabels[scope].label }}</span>
            </b-checkbox>
            <p class="help has-text-grey-dark">{{ scopeLabels[scope].description }}</p>
            <b-taglist v-if="scopeToolDetails[scope] && scopeToolDetails[scope].length" class="mt-3">
              <b-tag
                v-for="tool in scopeToolDetails[scope]"
                :key="tool.name"
                type="is-light"
              >
                {{ tool.label }}
              </b-tag>
            </b-taglist>
          </div>

          <b-message v-if="grantableScopes.indexOf('mcp:write') !== -1" type="is-info" size="is-small">
            La création et la modification sont sélectionnées si votre compte le permet.
            Décochez cette permission pour limiter l’assistant à la lecture et aux statistiques.
            Les publications et autres actions sensibles demanderont votre confirmation.
          </b-message>

        </template>
        <div class="buttons is-centered mt-5">
          <b-button class="has-text-black" :disabled="submitting" @click="deny">Refuser</b-button>
          <b-button
            v-if="canConnect"
            type="is-primary"
            class="has-text-black"
            :loading="submitting"
            :disabled="approvedScopes.length === 0"
            @click="approve"
          >
            Autoriser
          </b-button>
        </div>
      </div>
    </div>
  </section>
</template>
